/**
 * Formulaire — validation, marquage de l'obligatoire, et ARIA réellement branché.
 *
 * CE QUE CELA CORRIGE
 * -------------------
 * Trois manques, tous mesurés : aucun message d'erreur par champ dans toute l'application ;
 * `aria-invalid` et `aria-describedby` à zéro occurrence sur environ 135 champs, alors que
 * les primitives shadcn portent DÉJÀ le style d'erreur correspondant — écrit et jamais
 * déclenché ; et un astérisque saisi à la main à trois endroits seulement, quand les champs
 * facultatifs, eux, étaient signalés. L'inverse de la convention.
 *
 * UNE SEULE DÉCLARATION, TROIS EFFETS
 * -----------------------------------
 * `obligatoire: true` produit à la fois la règle, l'astérisque et `aria-required`. Il devient
 * impossible qu'un champ soit marqué obligatoire sans l'être, ou l'inverse.
 *
 * LE BOUTON N'EST PLUS DÉSACTIVÉ PAR LA VALIDITÉ
 * ----------------------------------------------
 * Soumettre un formulaire incomplet affiche les messages, déplace le focus sur le premier
 * champ fautif et récapitule ce qui manque. C'est ce qui remplace le bouton gris : au lieu
 * d'interdire l'action sans rien dire, on la laisse faire et on répond.
 */
import {
  useCallback, useId, useMemo, useState,
  type ChangeEvent, type FormEvent,
} from 'react'

import { useT } from '@/i18n/language'
import { requis as regleRequis, type Regle } from '@/lib/validation'

/** Déclaration d'un champ. */
export interface DefinitionChamp {
  readonly initial: string
  /** Étiquette visible, réutilisée telle quelle dans le récapitulatif d'erreurs. */
  readonly label: string
  /** Ajoute la règle `requis`, l'astérisque et `aria-required` d'un seul geste. */
  readonly obligatoire?: boolean
  readonly regles?: readonly Regle[]
  /** Aide permanente sous le champ ; devient une cible d'`aria-describedby`. */
  readonly aide?: string
}

export type Schema = Readonly<Record<string, DefinitionChamp>>

/** Attributs à étaler sur `<Input>`, `<Textarea>` ou `<SelectNatif>`. */
export interface ControleChamp {
  readonly id: string
  readonly name: string
  readonly value: string
  readonly onChange: (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => void
  readonly onBlur: () => void
  readonly 'aria-invalid'?: true
  readonly 'aria-describedby'?: string
  readonly 'aria-required'?: true
}

export interface EtatChamp {
  readonly nom: string
  readonly id: string
  readonly label: string
  readonly obligatoire: boolean
  readonly aide?: string
  readonly valeur: string
  /** Erreur AFFICHABLE : non nulle après le premier `blur` du champ, ou après soumission. */
  readonly erreur: string | null
  readonly controle: ControleChamp
  definir(v: string): void
}

export interface Formulaire<S extends Schema> {
  readonly champs: { readonly [K in keyof S]: EtatChamp }
  readonly valeurs: { readonly [K in keyof S]: string }
  /** Aucune règle n'échoue. Pour AFFICHER un récapitulatif, jamais pour désactiver. */
  readonly valide: boolean
  readonly soumis: boolean
  /** Étiquettes des champs en erreur, pour le récapitulatif `role="alert"`. */
  readonly resume: readonly string[]
  /** Vrai si tous les champs sont obligatoires : une mention globale vaut mieux que N étoiles. */
  readonly tousObligatoires: boolean
  soumettre(action: (valeurs: { [K in keyof S]: string }) => void): (e: FormEvent) => void
  reinitialiser(valeurs?: Partial<{ [K in keyof S]: string }>): void
}

export function useFormulaire<S extends Schema>(schema: S): Formulaire<S> {
  const { t } = useT()
  const prefixe = useId()

  const cles = useMemo(() => Object.keys(schema) as (keyof S & string)[], [schema])
  const [valeurs, setValeurs] = useState<Record<string, string>>(
    () => Object.fromEntries(cles.map((k) => [k, schema[k]!.initial])))
  const [touches, setTouches] = useState<Record<string, boolean>>({})
  const [soumis, setSoumis] = useState(false)

  // La validation est PURE et recalculée à chaque rendu : dupliquer les erreurs dans un état
  // les laisserait dériver des valeurs à la première mise à jour manquée.
  const erreurs = useMemo(() => {
    const out: Record<string, string | null> = {}
    for (const k of cles) {
      const def = schema[k]!
      const regles = def.obligatoire ? [regleRequis, ...(def.regles ?? [])] : (def.regles ?? [])
      out[k] = regles.reduce<string | null>(
        (deja, regle) => deja ?? regle(valeurs[k] ?? '', t), null)
    }
    return out
  }, [cles, schema, valeurs, t])

  const definir = useCallback((k: string, v: string) => {
    setValeurs((prec) => ({ ...prec, [k]: v }))
  }, [])

  const champs = useMemo(() => {
    const out: Record<string, EtatChamp> = {}
    for (const k of cles) {
      const def = schema[k]!
      const id = `${prefixe}-${k}`
      const brute = erreurs[k] ?? null
      // On n'accuse pas un champ que l'utilisateur n'a pas encore quitté : un message rouge
      // dès la première frappe reproche une saisie qui n'est pas finie.
      const affichable = (touches[k] || soumis) ? brute : null
      const decrit = [
        def.aide ? `${id}-aide` : null,
        affichable ? `${id}-err` : null,
      ].filter(Boolean).join(' ')

      out[k] = {
        nom: k,
        id,
        label: def.label,
        obligatoire: Boolean(def.obligatoire),
        ...(def.aide ? { aide: def.aide } : {}),
        valeur: valeurs[k] ?? '',
        erreur: affichable,
        definir: (v: string) => definir(k, v),
        controle: {
          id,
          name: k,
          value: valeurs[k] ?? '',
          onChange: (e) => definir(k, e.target.value),
          onBlur: () => setTouches((prec) => ({ ...prec, [k]: true })),
          // `aria-invalid="false"` ajouterait du bruit sans information : l'attribut est
          // ABSENT quand le champ va bien. Idem pour un `describedby` vide, que certains
          // lecteurs d'écran traitent comme une référence cassée.
          ...(affichable ? { 'aria-invalid': true as const } : {}),
          ...(decrit ? { 'aria-describedby': decrit } : {}),
          ...(def.obligatoire ? { 'aria-required': true as const } : {}),
        },
      }
    }
    return out as { [K in keyof S]: EtatChamp }
  }, [cles, schema, prefixe, erreurs, touches, soumis, valeurs, definir])

  const premiereEnErreur = cles.find((k) => erreurs[k])
  const valide = !premiereEnErreur

  const soumettre = useCallback(
    (action: (v: { [K in keyof S]: string }) => void) => (e: FormEvent): void => {
      e.preventDefault()
      setSoumis(true)
      if (premiereEnErreur) {
        // Emmener l'utilisateur là où le problème se trouve : c'est ce qui remplace
        // fonctionnellement le bouton grisé. Le focus fait lire le message par les lecteurs
        // d'écran, via `aria-describedby`.
        //
        // Le champ est retrouvé par son IDENTIFIANT, pas par une `ref` : les primitives du
        // design system sont de simples fonctions sous React 18, qui ignorent silencieusement
        // une `ref` transmise en prop. Passer par l'identifiant fonctionne quel que soit le
        // composant choisi par l'appelant. `useId` produit des identifiants contenant « : » —
        // `getElementById` les accepte, un sélecteur CSS non.
        document.getElementById(`${prefixe}-${premiereEnErreur}`)?.focus()
        return
      }
      action(valeurs as { [K in keyof S]: string })
    },
    [premiereEnErreur, valeurs, prefixe])

  const reinitialiser = useCallback((suite?: Partial<Record<string, string>>) => {
    setValeurs(Object.fromEntries(
      cles.map((k) => [k, suite?.[k] ?? schema[k]!.initial])))
    setTouches({})
    setSoumis(false)
  }, [cles, schema])

  return {
    champs,
    valeurs: valeurs as { [K in keyof S]: string },
    valide,
    soumis,
    resume: cles.filter((k) => erreurs[k]).map((k) => schema[k]!.label),
    // Calculé, jamais déclaré : la mention « tous les champs sont obligatoires » ne peut
    // pas mentir sur un formulaire qui aurait gagné un champ facultatif entre-temps.
    tousObligatoires: cles.length > 0 && cles.every((k) => schema[k]!.obligatoire),
    soumettre,
    reinitialiser,
  }
}
